import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Copy, RefreshCw, QrCode, Power, Link2, Send, MessageSquare, Trash2, Mail, Eye } from 'lucide-react';

const FRONTEND_ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';

export const FamilyPagePanel = ({ orderId, token, API }) => {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [status, setStatus] = useState(null);
  const [thread, setThread] = useState([]);
  const [reply, setReply] = useState('');
  const [update, setUpdate] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [deceasedName, setDeceasedName] = useState('');
  const [showQR, setShowQR] = useState(false);

  const familyUrl = status?.token ? `${FRONTEND_ORIGIN}/family/${status.token}` : '';

  const loadStatus = async () => {
    try {
      const r = await axios.get(`${API}/orders/${orderId}/family-page`, auth);
      setStatus(r.data); setAccessCode(r.data.access_code || ''); setDeceasedName(r.data.deceased_name || '');
    } catch (e) { /* ignore */ }
  };
  const loadThread = async () => {
    try { const r = await axios.get(`${API}/orders/${orderId}/family-thread`, auth); setThread(r.data.messages || []); }
    catch (e) { /* ignore */ }
  };
  useEffect(() => { loadStatus(); loadThread(); /* eslint-disable-next-line */ }, [orderId]);

  const enable = async () => { await axios.post(`${API}/orders/${orderId}/family-page/enable`, {}, auth); toast.success('Family page enabled'); loadStatus(); };
  const disable = async () => { if (!window.confirm('Turn off the family page for this order?')) return; await axios.post(`${API}/orders/${orderId}/family-page/disable`, {}, auth); toast.success('Family page turned off'); loadStatus(); };
  const regenerate = async () => { if (!window.confirm('Create a new link? The old link will stop working.')) return; await axios.post(`${API}/orders/${orderId}/family-page/regenerate`, {}, auth); toast.success('New link created'); loadStatus(); };
  const saveSettings = async (patch) => { const r = await axios.patch(`${API}/orders/${orderId}/family-page/settings`, patch, auth); toast.success('Saved'); loadStatus(); return r; };

  const copyLink = async () => { try { await navigator.clipboard.writeText(familyUrl); toast.success('Link copied'); } catch { toast.error('Copy failed'); } };
  const [sendingWelcome, setSendingWelcome] = useState(false);
  const emailLink = async () => {
    if (!window.confirm('Email the customer a welcome message with their family page link?')) return;
    setSendingWelcome(true);
    try {
      const r = await axios.post(`${API}/orders/${orderId}/family-page/send-welcome`, {}, auth);
      toast.success(`Welcome email sent to ${r.data.email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send email');
    } finally { setSendingWelcome(false); }
  };

  const postUpdate = async () => {
    if (!update.trim()) return;
    await axios.post(`${API}/orders/${orderId}/family-updates`, { text: update.trim() }, auth);
    toast.success('Update posted to family page'); setUpdate('');
  };
  const sendReply = async () => {
    if (!reply.trim()) return;
    const r = await axios.post(`${API}/orders/${orderId}/family-thread/reply`, { text: reply.trim() }, auth);
    toast.success(r.data?.email_sent ? 'Reply sent & emailed to customer' : 'Reply posted');
    setReply(''); loadThread();
  };
  const deleteMessage = async (messageId) => {
    if (!window.confirm('Delete this message? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/orders/${orderId}/family-thread/${messageId}`, auth);
      toast.success('Message deleted'); loadThread(); loadStatus();
    } catch (e) { toast.error('Could not delete'); }
  };

  return (
    status && status.feature_enabled === false ? null :
    <Card className="border-l-4 border-l-teal-500" data-testid="family-page-panel">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Link2 className="w-5 h-5 text-teal-600" /> Family Page
          {status?.unread_messages > 0 && <span className="ml-2 text-xs bg-red-500 text-white rounded-full px-2 py-0.5" data-testid="family-unread-badge">{status.unread_messages} new</span>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!status?.enabled ? (
          <div>
            <p className="text-sm text-slate-500 mb-3">Give the family a private page to follow progress, see the design and message you — no login needed.</p>
            <Button onClick={enable} className="bg-teal-600 hover:bg-teal-700" data-testid="family-enable-btn">Enable family page</Button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-slate-100 rounded px-2 py-1.5 text-xs overflow-x-auto whitespace-nowrap" data-testid="family-url">{familyUrl}</code>
              <Button size="sm" variant="outline" onClick={copyLink} data-testid="family-copy-link"><Copy className="w-4 h-4" /></Button>
              <Button size="sm" variant="outline" onClick={() => setShowQR(!showQR)} data-testid="family-qr-toggle"><QrCode className="w-4 h-4" /></Button>
            </div>
            {showQR && familyUrl && (
              <div className="flex justify-center py-2">
                <img alt="QR code" src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(familyUrl)}`} className="rounded border" />
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <Button size="sm" className="bg-teal-600 hover:bg-teal-700" onClick={emailLink} disabled={sendingWelcome} data-testid="family-email-link"><Mail className="w-4 h-4 mr-1" /> {sendingWelcome ? 'Sending…' : 'Email link to customer'}</Button>
              <Button size="sm" variant="outline" onClick={regenerate} data-testid="family-regenerate"><RefreshCw className="w-4 h-4 mr-1" /> New link</Button>
              <Button size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" onClick={disable} data-testid="family-disable"><Power className="w-4 h-4 mr-1" /> Turn off</Button>
            </div>

            {/* Family activity */}
            <div className="rounded-lg bg-slate-50 border p-3 text-sm" data-testid="family-activity">
              <div className="flex items-center gap-2 text-slate-700 font-medium mb-1.5"><Eye className="w-4 h-4 text-teal-600" /> Family activity</div>
              <div className="grid grid-cols-2 gap-y-1 gap-x-3 text-slate-600">
                <span>Link emailed</span>
                <span className="text-right font-medium">{status.link_sent ? (status.link_sent_at ? new Date(status.link_sent_at).toLocaleDateString() : 'Yes') : 'Not yet'}</span>
                <span>Times opened</span>
                <span className="text-right font-medium" data-testid="family-view-count">{status.view_count || 0}</span>
                <span>First opened</span>
                <span className="text-right font-medium">{status.first_viewed_at ? new Date(status.first_viewed_at).toLocaleString() : '—'}</span>
                <span>Last opened</span>
                <span className="text-right font-medium">{status.last_viewed_at ? new Date(status.last_viewed_at).toLocaleString() : '—'}</span>
                <span>Messages from family</span>
                <span className="text-right font-medium">{status.unread_messages > 0 ? `${status.unread_messages} unread` : (thread.filter(m => m.sender === 'family').length || 0)}</span>
              </div>
              {!status.view_count && <p className="text-xs text-slate-400 mt-2">They haven't opened their page yet.</p>}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
              <div>
                <label className="text-xs text-slate-500">Loved one's name (shown on family page)</label>
                <div className="flex gap-2 mt-1">
                  <Input value={deceasedName} onChange={(e) => setDeceasedName(e.target.value)} placeholder="e.g. William Jones" data-testid="family-deceased-name" />
                  <Button size="sm" variant="outline" onClick={() => saveSettings({ deceased_name: deceasedName })}>Save</Button>
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-500">Access code (optional)</label>
                <div className="flex gap-2 mt-1">
                  <Input value={accessCode} onChange={(e) => setAccessCode(e.target.value)} placeholder="e.g. last 4 of phone" data-testid="family-access-code" />
                  <Button size="sm" variant="outline" onClick={() => saveSettings({ access_code: accessCode })}>Save</Button>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-600 mt-5">
                <input type="checkbox" checked={!!status.do_not_contact} onChange={(e) => saveSettings({ do_not_contact: e.target.checked })} data-testid="family-do-not-contact" />
                Do not contact (exclude from emails)
              </label>
            </div>

            {/* Post update */}
            <div className="border-t pt-3">
              <label className="text-xs font-medium text-slate-500">Post an update visible to the family</label>
              <div className="flex gap-2 mt-1">
                <Input value={update} onChange={(e) => setUpdate(e.target.value)} placeholder="e.g. Your proof will be ready next week" data-testid="family-update-input" />
                <Button size="sm" onClick={postUpdate} data-testid="family-update-post">Post</Button>
              </div>
            </div>

            {/* What the family shared */}
            {(status.tribute || status.loved_one_photo) && (
              <div className="border-t pt-3" data-testid="family-shared-block">
                <div className="text-xs font-medium text-slate-500 mb-2">What the family shared about their loved one</div>
                <div className="flex gap-3 items-start">
                  {status.loved_one_photo && (
                    <img src={status.loved_one_photo} alt="Loved one" className="w-16 h-16 rounded-full object-cover border shrink-0" data-testid="family-shared-photo" />
                  )}
                  {status.tribute && <p className="text-sm text-slate-600 italic whitespace-pre-wrap">{status.tribute}</p>}
                </div>
              </div>
            )}

            {/* Thread */}
            <div className="border-t pt-3">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-600 mb-2"><MessageSquare className="w-4 h-4" /> Family messages</div>
              <div className="space-y-2 max-h-56 overflow-y-auto mb-2" data-testid="family-thread">
                {thread.length === 0 && <p className="text-xs text-slate-400">No messages yet.</p>}
                {thread.map((m) => (
                  <div key={m.id} className={`group relative text-sm rounded-lg px-3 py-2 ${m.sender === 'mason' ? 'bg-teal-50 ml-8' : 'bg-slate-100 mr-8'}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-[11px] text-slate-400">{m.sender === 'mason' ? m.sender_name : (m.sender_name || 'Family')} · {new Date(m.created_at).toLocaleString()}</div>
                      <button onClick={() => deleteMessage(m.id)} className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-red-500 shrink-0" title="Delete message" data-testid="family-message-delete">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="whitespace-pre-wrap text-slate-700">{m.text}</div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply to the family (emails them a link)" data-testid="family-reply-input" />
                <Button size="sm" onClick={sendReply} className="bg-teal-600 hover:bg-teal-700" data-testid="family-reply-send"><Send className="w-4 h-4" /></Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default FamilyPagePanel;
